import { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import * as XLSX from "xlsx";
import app from "../firebase/firebase";

// Dropdown components
import QualificationDropdown from "../components/forms/QualificationDropdown";
import RankDropdown from "../components/forms/RankDropdown";
import SubjectDropdown from "../components/forms/SubjectDropdown";

const db = getFirestore(app);

const collectionsMap = {
  beceSup: "beceSupervisors",
  beceExam: "beceExaminers",
  mockSup: "mockSSCESupervisors",
  mockExam: "mockSSCEExaminers",
};

const filterFields = {
  beceSup: ["qualification", "rank"],
  mockSup: ["qualification", "rank"],
  beceExam: ["qualification", "subject"],
  mockExam: ["qualification", "subject"],
};

export default function AdminNominationDashboard() {
  const [type, setType] = useState("beceSup");
  const [data, setData] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({});

  // column visibility
  const [visibleColumns, setVisibleColumns] = useState({});

  // Export toggle
  const [exportFilteredOnly, setExportFilteredOnly] = useState(true);

  // pagination
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // fetch selected collection
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const colRef = collection(db, collectionsMap[type]);
        const snap = await getDocs(colRef);
        const items = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setData(items);

        // Reset UI
        setFilters({});
        setSearchTerm("");
        setPage(1);

        if (items.length > 0) {
          const cols = {};
          Object.keys(items[0]).forEach((k) => {
            if (k !== "id") cols[k] = true;
          });
          setVisibleColumns(cols);
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    }
    fetchData();
  }, [type]);

  // fetch counts
  useEffect(() => {
    async function fetchCounts() {
      const c = {};
      for (const key of Object.keys(collectionsMap)) {
        try {
          const snap = await getDocs(collection(db, collectionsMap[key]));
          c[key] = snap.size;
        } catch {
          c[key] = 0;
        }
      }
      setCounts(c);
    }
    fetchCounts();
  }, []);

  // multi-filter updates
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
    setPage(1);
  };

  // filtered, searched, sorted dataset
  const processedData = useMemo(() => {
    let filtered = [...data];

    // apply filters
    Object.entries(filters).forEach(([field, value]) => {
      if (value && value.length > 0) {
        filtered = filtered.filter((item) =>
          Array.isArray(value)
            ? value.includes(item[field])
            : item[field]?.toLowerCase().includes(value.toLowerCase())
        );
      }
    });

    // global search
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        Object.values(row).some((val) =>
          JSON.stringify(val).toLowerCase().includes(s)
        )
      );
    }

    return filtered;
  }, [data, filters, searchTerm]);

  const totalPages = Math.ceil(processedData.length / pageSize);
  const paginatedData = processedData.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // EXPORT -----------------------------------------------------
  const exportExcel = () => {
    const exportData = exportFilteredOnly ? processedData : data;

    const rows = [];

    exportData.forEach((entry) => {
      // flatten nominees (if exists)
      if (Array.isArray(entry.nominees)) {
        entry.nominees.forEach((n) => {
          rows.push({
            School: entry.school,
            Principal: entry.principal,
            Name: n.name,
            Qualification: n.qualification,
            Subject: n.subject ?? "",
            Rank: n.rank ?? "",
            Experience: n.experience,
            Phone: n.phone,
            SubmittedAt: entry.submittedAt?.seconds
              ? new Date(entry.submittedAt.seconds * 1000).toLocaleString()
              : "",
          });
        });
      } else {
        // supervisor: single row
        rows.push({
          School: entry.school,
          Principal: entry.principal,
          Name: entry.name,
          Qualification: entry.qualification,
          Subject: entry.subject ?? "",
          Rank: entry.rank ?? "",
          Experience: entry.experience,
          Phone: entry.phone ?? "",
          Centre: entry.centre ?? "",
          SubmittedAt: entry.submittedAt?.seconds
            ? new Date(entry.submittedAt.seconds * 1000).toLocaleString()
            : "",
        });
      }
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");

    XLSX.writeFile(wb, `${collectionsMap[type]}-export.xlsx`);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h2 className="text-3xl font-bold text-center mb-6">
        Nomination Dashboard
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card label="BECE Supervisors" count={counts.beceSup} color="blue" />
        <Card label="BECE Examiners" count={counts.beceExam} color="green" />
        <Card
          label="MOCK SSCE Supervisors"
          count={counts.mockSup}
          color="blue"
        />
        <Card
          label="MOCK SSCE Examiners"
          count={counts.mockExam}
          color="green"
        />
      </div>

      {/* Type selector */}
      <div className="mb-4 flex justify-center">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="border px-3 py-2 rounded"
        >
          <option value="beceSup">BECE Supervisors</option>
          <option value="beceExam">BECE Examiners</option>
          <option value="mockSup">MOCK SSCE Supervisors</option>
          <option value="mockExam">MOCK SSCE Examiners</option>
        </select>
      </div>

      {/* Filters */}
      <div className="bg-gray-100 p-2 rounded mb-4 flex flex-wrap gap-4 justify-center">
        {filterFields[type].includes("qualification") && (
          <QualificationDropdown
          showLabel={false}
            value={filters.qualification || ""}
            onChange={(v) => handleFilterChange("qualification", v)}
          />
        )}

        {filterFields[type].includes("rank") && (
          <RankDropdown
            showLabel={false}
            value={filters.rank || ""}
            onChange={(v) => handleFilterChange("rank", v)}
          />
        )}

        {filterFields[type].includes("subject") && (
          <SubjectDropdown
            showLabel={false}
            value={filters.subject || ""}
            onChange={(v) => handleFilterChange("subject", v)}
          />
        )}

        <input
          type="text"
          placeholder="Search..."
          className="border px-2 py-1 rounded"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <button
          className="bg-red-500 text-white px-2 py-1 rounded"
          onClick={clearFilters}
        >
          Clear Filters
        </button>
      </div>

      {/* Column visibility */}
      {data.length > 0 && (
        <div className="bg-gray-50 p-3 rounded mb-4">
          <h4 className="font-medium mb-2">Visible Columns</h4>
          <div className="flex flex-wrap gap-3">
            {Object.keys(visibleColumns).map((col) => (
              <label key={col} className="flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={visibleColumns[col]}
                  onChange={() =>
                    setVisibleColumns((prev) => ({
                      ...prev,
                      [col]: !prev[col],
                    }))
                  }
                />
                {col}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Export buttons */}
      <div className="flex justify-between mb-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={exportFilteredOnly}
            onChange={() => setExportFilteredOnly((x) => !x)}
          />
          Export filtered only
        </label>

        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={exportExcel}
        >
          Export to Excel
        </button>
      </div>

      {/* Data table */}
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {Object.keys(visibleColumns)
                  .filter((c) => visibleColumns[c])
                  .map((col) => (
                    <th key={col} className="border px-2 py-1 bg-gray-200">
                      {col}
                    </th>
                  ))}
              </tr>
            </thead>

            <tbody>
              {paginatedData.map((row) => (
                <tr key={row.id}>
                  {Object.entries(row).map(([key, val]) => {
                    if (!visibleColumns[key]) return null;

                    // expand nominee arrays in table
                    if (Array.isArray(val)) {
                      return (
                        <td key={key} className="border px-2 py-1">
                          {val.map((n, i) => (
                            <div key={i}>
                              {Object.entries(n)
                                .map(([a, b]) => `${a}: ${b}`)
                                .join(", ")}
                            </div>
                          ))}
                        </td>
                      );
                    }

                    return (
                      <td key={key} className="border px-2 py-1">
                        {val?.seconds
                          ? new Date(val.seconds * 1000).toLocaleString()
                          : val}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-center gap-3 mt-4">
        <button
          disabled={page === 1}
          onClick={() => setPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Prev
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          disabled={page === totalPages}
          onClick={() => setPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function Card({ label, count, color }) {
  return (
    <div className={`bg-${color}-600 text-white rounded p-4 text-center`}>
      <h3 className="text-lg font-semibold">{label}</h3>
      <p className="text-2xl font-bold">{count || 0}</p>
    </div>
  );
}
